<script setup>
import { ref, watch } from 'vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  title: { type: String, required: true },
  modelValue: { type: String, default: '' },
  example: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  tooltip: { type: String, default: '' },
  isOpen: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const isExpanded = ref(props.isOpen);
const localValue = ref(props.modelValue);

const toggleAccordion = () => {
  isExpanded.value = !isExpanded.value;
};

watch(
  () => props.modelValue,
  newValue => {
    localValue.value = newValue;
  }
);

watch(localValue, newValue => {
  emit('update:modelValue', newValue);
});

watch(
  () => props.isOpen,
  newValue => {
    isExpanded.value = newValue;
  }
);
</script>

<template>
  <div
    class="border rounded-xl border-n-slate-3 overflow-hidden bg-white dark:bg-n-slate-1"
  >
    <!-- Header -->
    <button
      class="flex items-center justify-between w-full px-5 py-4 text-left transition-colors bg-n-slate-1 dark:bg-n-slate-2 hover:bg-n-slate-2 dark:hover:bg-n-slate-3"
      @click="toggleAccordion"
    >
      <div class="flex items-center gap-2">
        <span
          class="w-5 h-5 transition-transform duration-200 text-n-slate-11 i-lucide-chevron-down"
          :class="{ 'rotate-180': isExpanded }"
        />
        <span
          class="text-sm font-semibold tracking-wide text-n-slate-12 uppercase"
        >
          {{ title }}
        </span>
        <!-- Tooltip Icon -->
        <div v-if="tooltip" class="relative group">
          <span class="w-4 h-4 text-n-slate-9 i-lucide-info cursor-help" />
          <div
            class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-n-slate-12 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-64 z-50"
          >
            {{ tooltip }}
            <div
              class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-n-slate-12"
            />
          </div>
        </div>
      </div>
    </button>

    <!-- Content -->
    <div v-show="isExpanded" class="px-5 py-4 border-t border-n-slate-3">
      <div class="flex flex-col gap-3">
        <!-- Label -->
        <label class="text-sm font-medium text-n-slate-11">
          Your Append:
        </label>

        <!-- Textarea -->
        <Textarea
          v-model="localValue"
          :placeholder="placeholder"
          :rows="4"
          class="w-full"
        />

        <!-- Example -->
        <div v-if="example" class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-11">Example:</span>
          <p class="text-sm text-n-slate-10 whitespace-pre-line">
            {{ example }}
          </p>
        </div>

        <!-- Warning -->
        <p class="text-sm text-amber-600 dark:text-amber-500 font-medium">
          Please take note that curly brackets will be removed upon saving.
        </p>
      </div>
    </div>
  </div>
</template>
